import React, { useState } from 'react';
import { ensureSessionHeaders } from '../lib/serverSession';

import { getUserIdFromObject, assertValidUserId, extractNumericId } from '../lib/userIdExtractor';
import { isMasterAdminViewSession } from '../lib/masterAdminAccess';
import { ReferralAutocomplete } from './ReferralAutocomplete';
import PurchaseRewardPreview from './PurchaseRewardPreview';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Add spinner animation styles
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}

interface CurrentPhase {
  id: number;
  phase_number: number;
  phase_name: string;
  price_per_share: number;
  total_shares_available: number;
  shares_sold: number;
  is_active: boolean;
}

interface User {
  id: number | string;
  email: string;
  username?: string;
  full_name?: string;
  user?: {
    id: string;
  };
  database_user?: {
    id: number;
    username?: string;
    full_name?: string;
    email: string;
  };
  user_metadata?: {
    user_id?: number;
    full_name?: string;
    username?: string;
  };
}

interface BankTransferStepProps {
  amount: string;
  shares: number;
  currentPhase: CurrentPhase | null;
  user: User | null;
  onBack: () => void;
  onComplete: () => void;
  bonusShares?: number;
  totalShares?: number;
  // Resume/resubmit payment support
  existingPaymentId?: string; // ID of existing payment to update (for resume)
  isResubmit?: boolean; // If true, create new record even if existingPaymentId provided
  // Balance payment support (for underpaid payments)
  isBalancePayment?: boolean; // If true, this is a balance payment for an underpaid transaction
  originalPaymentId?: string; // ID of the original underpaid payment
  balanceOwed?: number; // The exact balance amount owed
  // Birthday 2026 package metadata (carried into transaction_notes for approval)
  birthdayPackage?: 'BIRTHDAY_2' | 'BIRTHDAY_25';
  birthdayQty?: number;
  productionCapitalPackage?: 'P1' | 'P2' | 'P3' | 'P4' | 'P5';
  productionCapitalPackageQty?: number;
  productionCapitalPackages?: Array<{ packageId: 'P1' | 'P2' | 'P3' | 'P4' | 'P5'; qty: number }>;
  singleShareActive?: boolean;
}

const BankTransferStep: React.FC<BankTransferStepProps> = ({
  shares,
  currentPhase,
  user,
  onBack,
  onComplete,
  bonusShares = 0,
  totalShares = 0,
  existingPaymentId,
  isResubmit = false,
  isBalancePayment = false,
  originalPaymentId,
  balanceOwed,
  birthdayPackage,
  birthdayQty,
  productionCapitalPackage,
  productionCapitalPackageQty,
  productionCapitalPackages,
  singleShareActive = false,
}) => {
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState('');

  // Confirmation dialog state
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Gift purchase state
  const [isGiftPurchase, setIsGiftPurchase] = useState(false);
  const [recipientUsername, setRecipientUsername] = useState('');
  const [recipientValidation, setRecipientValidation] = useState<{
    isValidating: boolean;
    isValid: boolean | null;
    recipientName: string | null;
    recipientId: number | null;
  }>({
    isValidating: false,
    isValid: null,
    recipientName: null,
    recipientId: null
  });

  // Bank transfer configuration (matching Telegram bot)
  const bankTransferConfig = {
    exchangeRate: 17, // USD to ZAR
    transactionFee: 0.10 // 10% transaction fee
  };

  // SPECIAL PRICING: User 106 gets $1 for exactly 1 share (testing)
  const TEST_USER_ID = 106;
  const TEST_PRICE_SINGLE_SHARE = 1;

  // Extract user ID for special pricing using centralized utility
  const numericUserId = extractNumericId(user?.database_user?.id)
    || extractNumericId(user?.user_metadata?.user_id)
    || extractNumericId(user?.id);

  const isSpecialPricingActive = numericUserId === TEST_USER_ID && shares === 1;

  // Calculate total cost with special pricing check
  const normalCost = shares * (currentPhase?.price_per_share || 0);
  const totalCost = isSpecialPricingActive ? TEST_PRICE_SINGLE_SHARE : normalCost;

  // Calculate ZAR amounts (no commission for bank transfers)
  const zarAmount = totalCost * bankTransferConfig.exchangeRate;
  const feeAmount = zarAmount * bankTransferConfig.transactionFee;
  const totalZarAmount = zarAmount + feeAmount;

  console.log('🏦 Bank Transfer - Special Pricing Check:', {
    userId: numericUserId,
    shares,
    isSpecialPricingActive,
    normalCost,
    totalCost,
    totalZarAmount
  });

  // Get or generate temporary bank reference
  // This ensures users see the same reference if they navigate away and come back
  const getOrGenerateTempReference = async (userIdInput: any, username: string) => {
    try {
      // Get the integer user ID from the user object
      const userId = await getUserIdFromObject(userIdInput);
      console.log('🔍 [TEMP REF] Extracted user ID:', userId, 'Type:', typeof userId);

      if (!userId) {
        console.error('❌ Could not get user ID for temp reference');
        // Fallback to generating without storing - use 4-digit random number
        const randomNum = Math.floor(1000 + Math.random() * 9000);
        const cleanUsername = username ? username.replace('@', '').toUpperCase() : '';
        const userPrefix = cleanUsername.length >= 3 ? cleanUsername.substring(0, 3) : (cleanUsername.padEnd(3, '0') || 'USR');
        return `AUR${randomNum}-${userPrefix}`;
      }

      // Validate that we have an integer user ID
      if (typeof userId !== 'number') {
        console.error('❌ User ID is not a number:', userId, 'Type:', typeof userId);
        // Fallback to generating without storing - use 4-digit random number
        const randomNum = Math.floor(1000 + Math.random() * 9000);
        const cleanUsername = username ? username.replace('@', '').toUpperCase() : '';
        const userPrefix = cleanUsername.length >= 3 ? cleanUsername.substring(0, 3) : (cleanUsername.padEnd(3, '0') || 'USR');
        return `AUR${randomNum}-${userPrefix}`;
      }

      // Get the auth UUID for querying (RLS requires auth_user_id)
      const authUserId = userIdInput?.id || userIdInput?.user?.id;
      console.log('🔍 [TEMP REF] Auth UUID:', authUserId);
      console.log('🔍 [TEMP REF] Integer user ID:', userId);

      if (!authUserId) {
        console.error('❌ Could not get auth UUID for temp reference query');
        // Fallback to generating without storing - use 4-digit random number
        const randomNum = Math.floor(1000 + Math.random() * 9000);
        const cleanUsername = username ? username.replace('@', '').toUpperCase() : '';
        const userPrefix = cleanUsername.length >= 3 ? cleanUsername.substring(0, 3) : (cleanUsername.padEnd(3, '0') || 'USR');
        return `AUR${randomNum}-${userPrefix}`;
      }

      // Step 1: Check if user already has a temporary reference (via server API to bypass RLS)
      try {
        const refResponse = await fetch('/api/purchase/temp-reference', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
          body: JSON.stringify({ authUserId: userId, action: 'get' })
        });
        const refResult = await refResponse.json();

        if (refResult.success && refResult.reference) {
          console.log('✅ Reusing existing temp reference:', refResult.reference);
          return refResult.reference;
        }
      } catch (fetchErr) {
        console.warn('⚠️ [TEMP REF] API fetch failed, will generate new one:', fetchErr);
      }

      // Step 2: No temp reference exists - generate a new one
      console.log('🔄 Generating new temp reference...');

      // Get first 3 characters of username (uppercase)
      let userPrefix = '';
      if (username && username.length > 0) {
        const cleanUsername = username.replace('@', '').toUpperCase();
        if (cleanUsername.length >= 3) {
          userPrefix = cleanUsername.substring(0, 3);
        } else {
          userPrefix = cleanUsername.padEnd(3, '0');
        }
      } else {
        userPrefix = 'USR';
      }

      // Generate unique reference with AUR prefix + 4-digit random + user prefix
      const randomNum = Math.floor(1000 + Math.random() * 9000);
      const reference = `AUR${randomNum}-${userPrefix}`;

      // Store the new temp reference via API endpoint (bypasses RLS)
      try {
        const response = await fetch('/api/purchase/temp-reference', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
          body: JSON.stringify({ authUserId: userId, reference, action: 'set' })
        });

        const result = await response.json();

        if (result.success) {
          console.log('✅ Stored new temp reference via API:', reference);
        } else {
          console.warn('⚠️ [TEMP REF] Failed to store reference:', result.error);
        }
      } catch (apiError) {
        console.warn('⚠️ [TEMP REF] API error storing reference:', apiError);
      }

      return reference;

    } catch (error) {
      console.error('❌ Error in getOrGenerateTempReference:', error);
      // Fallback reference - use 4-digit random number
      const randomNum = Math.floor(1000 + Math.random() * 9000);
      const cleanUsername = username ? username.replace('@', '').toUpperCase() : '';
      const userPrefix = cleanUsername.length >= 3 ? cleanUsername.substring(0, 3) : (cleanUsername.padEnd(3, '0') || 'USR');
      return `AUR${randomNum}-${userPrefix}`;
    }
  };

  const [referenceNumber, setReferenceNumber] = React.useState<string>('Generating...');

  // Get or generate reference on component mount
  const loadingRef = React.useRef(false);

  React.useEffect(() => {
    const loadReference = async () => {
      if (user && !loadingRef.current) {
        loadingRef.current = true;
        console.log('🔄 [TEMP REF] Loading reference for user:', user?.username || user?.email);
        const ref = await getOrGenerateTempReference(user, user?.username || user?.email || 'USR');
        console.log('🔄 [TEMP REF] Got reference:', ref);
        if (ref) {
          setReferenceNumber(ref);
        } else {
          console.error('❌ [TEMP REF] Reference is null/undefined');
        }
        loadingRef.current = false;
      }
    };
    loadReference();
  }, [user]);

  // Company banking details
  const bankingDetails = {
    bankName: 'First National Bank (FNB)',
    accountName: 'AUREUS ALLIANCE HOLDINGS (PTY) LTD',
    accountNumber: '63154323041',
    branchCode: '250655',
    accountType: 'Business Cheque Account',
    reference: referenceNumber
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  // Validate recipient username
  const validateRecipientUsername = async (username: string) => {
    if (!username.trim()) {
      setRecipientValidation({
        isValidating: false,
        isValid: null,
        recipientName: null,
        recipientId: null
      });
      return;
    }

    setRecipientValidation({
      isValidating: true,
      isValid: null,
      recipientName: null,
      recipientId: null
    });

    try {
      // Use API endpoint to validate recipient (bypasses RLS server-side)
      const response = await fetch('/api/purchase/validate-recipient', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
        body: JSON.stringify({ username: username.trim() })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        console.error('Error validating recipient username:', result.error);
        setRecipientValidation({
          isValidating: false,
          isValid: false,
          recipientName: null,
          recipientId: null
        });
        return;
      }

      if (!result.isValid) {
        // v2.9.103 + v4.0.47: Show specific message for self-purchase or sponsor-purchase attempt
        const selfPurchaseMsg = result.isSelf
          ? 'You cannot purchase shares for yourself. Simply purchase shares normally instead.'
          : result.isSponsor
          ? 'You cannot purchase shares for your sponsor or any upline sponsor. The shares would be assigned to them instead of you.'
          : null;
        setRecipientValidation({
          isValidating: false,
          isValid: false,
          recipientName: selfPurchaseMsg,
          recipientId: null
        });
      } else {
        setRecipientValidation({
          isValidating: false,
          isValid: true,
          recipientName: result.recipientName,
          recipientId: result.recipientId
        });
      }
    } catch (error) {
      console.error('Error validating recipient username:', error);
      setRecipientValidation({
        isValidating: false,
        isValid: false,
        recipientName: null,
        recipientId: null
      });
    }
  };

  // Handle recipient username change with debounce
  const handleRecipientUsernameChange = (value: string) => {
    setRecipientUsername(value);

    // Debounce validation
    const timeoutId = setTimeout(() => {
      validateRecipientUsername(value);
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  // Compress image before upload to improve speed
  const compressImage = async (file: File): Promise<File> => {
    // Only compress images, not PDFs
    if (!file.type.startsWith('image/')) {
      return file;
    }

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Resize if image is too large (max 1920px width)
          const maxWidth = 1920;
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          // Convert to blob with compression (0.8 quality)
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                console.log(`📦 Compressed: ${(file.size / 1024).toFixed(0)}KB → ${(compressedFile.size / 1024).toFixed(0)}KB`);
                resolve(compressedFile);
              } else {
                resolve(file);
              }
            },
            'image/jpeg',
            0.8
          );
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a valid file (JPG, PNG, WEBP, PDF)');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }

      // Compress image if it's an image file
      setUploadProgress('Preparing file...');
      try {
        const processedFile = await compressImage(file);
        setProofFile(processedFile);
        setError('');
        setUploadProgress('');
      } catch (err) {
        console.error('File processing error:', err);
        setProofFile(file); // Use original if compression fails
        setError('');
        setUploadProgress('');
      }
    }
  };

  // Show confirmation dialog
  const handleShowConfirmation = () => {
    if (!proofFile) {
      setError('Please upload payment proof');
      return;
    }

    // Validate gift purchase if enabled
    if (isGiftPurchase) {
      if (!recipientUsername.trim()) {
        setError('Please enter the recipient username');
        return;
      }

      if (recipientValidation.isValid !== true) {
        setError(`Username "${recipientUsername}" not found. Please check the spelling and try again.`);
        return;
      }

      console.log('✅ BANK TRANSFER: Gift purchase validated for:', recipientValidation.recipientName);
    }

    console.log('✅ BANK TRANSFER: Validation passed, showing confirmation');
    setError('');
    setShowConfirmation(true);
  };

  // Actual payment submission (called after confirmation)
  const handleSubmitPayment = async () => {
    // 🔒 ADMIN SAFETY: Block actual submission for master admin testing sessions
    if (isMasterAdminViewSession()) {
      console.log('🛑 ADMIN TEST MODE: Bank payment submission blocked — admin testing only');
      setShowConfirmation(false);
      setError('🛑 Admin Test Mode: Payment submission is disabled. You have successfully tested the full purchase flow up to the confirmation screen.');
      return;
    }

    console.log('🚀 BANK TRANSFER: User confirmed purchase');
    setShowConfirmation(false);

    const startTime = Date.now();
    console.log('⏱️ [TIMING] Payment submission started at:', new Date().toISOString());

    setLoading(true);
    setError('');
    setUploadProgress('Uploading proof of payment...');

    try {
      // Validate proof file exists
      if (!proofFile) {
        throw new Error('Please upload proof of payment');
      }

      // Upload proof file via server-side API (bypasses RLS for database-only sessions)
      const fileExt = proofFile.name.split('.').pop();
      const timestamp = Date.now();
      const fileName = `payment_${user?.id}_${timestamp}.${fileExt}`;

      console.log('📤 Uploading file via server API:', fileName);
      console.log('📦 File size:', (proofFile.size / 1024).toFixed(0), 'KB');

      const uploadStartTime = Date.now();

      // Convert file to base64 for server-side upload
      const fileBuffer = await proofFile.arrayBuffer();
      const base64Data = btoa(
        new Uint8Array(fileBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
      );

      // Upload with timeout (30 seconds)
      const uploadPromise = fetch('/api/purchase/upload-bank-proof', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
        body: JSON.stringify({
          fileData: base64Data,
          fileName,
          contentType: proofFile.type
        })
      });

      const timeoutPromise = new Promise<Response>((_, reject) =>
        setTimeout(() => reject(new Error('Upload timeout - please check your internet connection')), 30000)
      );

      const uploadResponse = await Promise.race([uploadPromise, timeoutPromise]);
      const uploadResult = await uploadResponse.json();

      const uploadEndTime = Date.now();
      console.log(`⏱️ [TIMING] File upload took: ${uploadEndTime - uploadStartTime}ms`);

      if (!uploadResult.success) {
        console.error('Upload error:', uploadResult);
        throw new Error('Failed to upload proof file: ' + (uploadResult.details || uploadResult.error));
      }

      console.log('✅ File uploaded successfully');
      setUploadProgress('Processing payment...');

      const fileUrl = uploadResult.storagePath;
      console.log('📎 File URL:', fileUrl);

      // Get the integer user ID using centralized utility
      const userLookupStartTime = Date.now();
      console.log('🔍 DEBUG: User object in BankTransferStep:', {
        hasUser: !!user,
        userId: user?.id,
        userKeys: user ? Object.keys(user) : [],
        hasUserMetadata: !!user?.user_metadata,
        userMetadataUserId: user?.user_metadata?.user_id
      });

      const userId = await getUserIdFromObject(user);

      console.log(`⏱️ [TIMING] User lookup took: ${Date.now() - userLookupStartTime}ms`);

      if (!userId) {
        console.error('Error getting user ID from user object:', user);
        throw new Error('User not found. Please ensure you are properly logged in.');
      }

      // Validate that we have a proper integer user ID
      assertValidUserId(userId, 'bank transfer payment creation');

      // Check if this is an admin-assisted purchase
      const { getMasterAdminViewSession } = await import('../lib/masterAdminAccess');
      const adminSession = getMasterAdminViewSession();
      const isAdminAssisted = adminSession && adminSession.permissions?.canPurchase;

      if (isAdminAssisted) {
        console.log('🤝 BANK TRANSFER: Admin-assisted purchase detected');
        console.log('🤝 BANK TRANSFER: Admin:', adminSession.adminEmail);
        console.log('🤝 BANK TRANSFER: Helping user:', adminSession.targetEmail);
      }

      // Determine recipient user ID (for gift purchases)
      const recipientUserId = isGiftPurchase && recipientValidation.recipientId
        ? recipientValidation.recipientId
        : userId;

      const purchaserUserId = userId; // The person making the payment

      if (isGiftPurchase) {
        console.log('🎁 BANK TRANSFER: Gift purchase detected');
        console.log('🎁 BANK TRANSFER: Purchaser ID:', purchaserUserId);
        console.log('🎁 BANK TRANSFER: Recipient ID:', recipientUserId);
        console.log('🎁 BANK TRANSFER: Recipient:', recipientValidation.recipientName);
      }

      // Create or update bank transfer payment transaction directly in Supabase
      const dbInsertStartTime = Date.now();
      // For balance payments, append "-BALANCE" to reference number
      const effectiveReferenceNumber = isBalancePayment ? `${referenceNumber}-BALANCE` : referenceNumber;

      const paymentData = {
        user_id: recipientUserId, // Shares go to recipient
        amount: totalCost,
        shares_to_purchase: shares,
        network: 'BANK_TRANSFER',
        currency: 'ZAR',
        sender_wallet: fileUrl, // Store proof file URL in sender_wallet field
        receiver_wallet: bankingDetails.accountNumber,
        screenshot_url: fileUrl,
        status: 'pending',
        transaction_notes: JSON.stringify({
          payment_method: 'bank_transfer',
          zar_amount: totalZarAmount,
          exchange_rate: bankTransferConfig.exchangeRate,
          transaction_fee_percent: bankTransferConfig.transactionFee * 100,
          reference_number: effectiveReferenceNumber,
          admin_assisted: isAdminAssisted,
          admin_email: isAdminAssisted ? adminSession.adminEmail : null,
          // Add gift purchase metadata
          is_gift_purchase: isGiftPurchase,
          purchased_by_user_id: isGiftPurchase ? purchaserUserId : null,
          purchased_by_username: isGiftPurchase ? (user?.username || user?.database_user?.username) : null,
          recipient_username: isGiftPurchase ? recipientUsername : null,
          recipient_name: isGiftPurchase ? recipientValidation.recipientName : null,
          bonus_shares: bonusShares,
          total_shares: totalShares,
          // Balance payment metadata (for underpaid payments)
          ...(isBalancePayment && originalPaymentId && {
            is_balance_payment: true,
            original_payment_id: originalPaymentId,
            balance_amount: balanceOwed || totalCost
          }),
          // Birthday 2026 package tag — read by api/admin/approve-payment.js to
          // issue the commission-free buyer + upline gifts on approval.
          ...(birthdayPackage && {
            birthday_package: birthdayPackage,
            birthday_qty: birthdayQty || 1
          }),
          ...(productionCapitalPackage && {
            campaign_id: 'PRODUCTION_CAPITAL_2026',
            campaign_package: productionCapitalPackage,
            campaign_package_qty: productionCapitalPackageQty || 1,
            campaign_cart_explicit: true,
            campaign_packages: (productionCapitalPackages && productionCapitalPackages.length
              ? productionCapitalPackages
              : [{ packageId: productionCapitalPackage, qty: productionCapitalPackageQty || 1 }])
          })
        }),
        // Add admin assistance metadata
        admin_assisted: isAdminAssisted
      };

      const progressMsg = isBalancePayment
        ? 'Creating balance payment record...'
        : (existingPaymentId && !isResubmit ? 'Updating payment record...' : 'Creating payment record...');
      setUploadProgress(progressMsg);

      // Submit payment via API endpoint (bypasses RLS server-side)
      const authUserId = user?.id || user?.user?.id;

      let response: Response;
      let result: any;
      try {
        const submitPromise = fetch(`${API_BASE_URL}/api/purchase/submit-bank-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
          body: JSON.stringify({
            paymentData,
            existingPaymentId,
            isResubmit,
            isBalancePayment,
            authUserId // API will also clear temp reference
          })
        });

        const submitTimeout = new Promise<Response>((_, reject) =>
          setTimeout(() => reject(new Error('Payment submission timed out. Please check your internet connection and try again.')), 60000)
        );

        response = await Promise.race([submitPromise, submitTimeout]);
        result = await response.json();
      } catch (fetchErr: any) {
        console.error('❌ Fetch error submitting bank payment:', fetchErr);
        throw new Error(
          fetchErr?.message?.includes('timed out')
            ? fetchErr.message
            : 'Network error while submitting payment. Please check your internet connection and try again.'
        );
      }

      console.log(`⏱️ [TIMING] Database operation took: ${Date.now() - dbInsertStartTime}ms`);

      if (!response.ok || !result.success) {
        console.error('Payment error:', result.error);
        throw new Error('Failed to process payment: ' + (result.error || 'Unknown error'));
      }

      const paymentResult = result.payment;
      console.log('✅ Bank transfer payment processed:', paymentResult);

      const totalTime = Date.now() - startTime;
      console.log(`⏱️ [TIMING] TOTAL PAYMENT SUBMISSION TIME: ${totalTime}ms (${(totalTime/1000).toFixed(2)}s)`);

      // Send transaction notification email for bank transfer (TRULY non-blocking - fire and forget)
      if (paymentResult) {
        // Fire and forget - don't await, don't block UI
        setTimeout(async () => {
          try {
            const response = await fetch(`${API_BASE_URL}/api/purchase/notify-bank-transfer`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
              body: JSON.stringify({
                transactionId: paymentResult.id,
                userId: purchaserUserId,
                amount: totalCost,
                currency: 'ZAR',
                shareQuantity: shares,
                bankReference: referenceNumber,
                proofUrl: fileUrl
              })
            });
            if (response.ok) {
              console.log('📧 Transaction notification sent for bank transfer:', paymentResult.id);
            } else {
              console.error('❌ Failed to send transaction notification:', await response.text());
            }
          } catch (notificationError) {
            console.error('❌ Failed to send transaction notification:', notificationError);
          }
        }, 0);
      }

      console.log('⏱️ Completing payment flow at:', new Date().toISOString());
      setUploadProgress('Complete!');

      // Small delay to show "Complete!" message
      await new Promise(resolve => setTimeout(resolve, 500));

      onComplete();
    } catch (err) {
      console.error('Payment submission error:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit payment');
      setUploadProgress('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px', textAlign: 'center' }}>
        {isBalancePayment ? '💳 Balance Payment (ZAR)' : '🏦 Bank Transfer Payment (ZAR)'}
      </h2>

      {/* Balance Payment Notice Banner */}
      {isBalancePayment && (
        <div style={{
          backgroundColor: 'rgba(245, 158, 11, 0.2)',
          border: '2px solid #f59e0b',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <div style={{ width: '36px', height: '36px', background: '#f59e0b', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
              ⚠️
            </div>
            <div style={{ color: '#f59e0b', fontSize: '16px', fontWeight: 'bold' }}>
              Balance Payment Required
            </div>
          </div>
          <div style={{ color: '#fbbf24', fontSize: '14px', lineHeight: '1.6' }}>
            <p style={{ margin: '0 0 8px 0' }}>
              This is a <strong>balance payment</strong> for an underpaid transaction.
            </p>
            <p style={{ margin: '0 0 8px 0' }}>
              <strong>Balance Due:</strong> ${(balanceOwed || 0).toFixed(2)}
            </p>
            <p style={{ margin: 0, fontSize: '13px', color: '#ffffff' }}>
              Use reference: <strong style={{ fontFamily: 'monospace', color: '#fbbf24' }}>{referenceNumber}-BALANCE</strong>
            </p>
          </div>
        </div>
      )}

      {/* Special Pricing Banner for User 106 */}
      {isSpecialPricingActive && (
        <div style={{
          backgroundColor: 'rgba(245, 158, 11, 0.15)',
          border: '2px solid #f59e0b',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          <div style={{ color: '#f59e0b', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
            🎯 SPECIAL TEST PRICING ACTIVE
          </div>
          <div style={{ color: '#fbbf24', fontSize: '14px' }}>
            $1.00 for 1 share (normally ${normalCost.toFixed(2)}) = R{totalZarAmount.toFixed(2)}
          </div>
        </div>
      )}

      {/* Payment Summary */}
      <div style={{
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        border: '1px solid rgba(34, 197, 94, 0.3)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <h3 style={{ color: '#10b981', fontWeight: 'bold', margin: '0 0 16px 0' }}>
          💰 PAYMENT SUMMARY:
        </h3>
        <div style={{ color: '#ffffff', fontSize: '14px', lineHeight: '1.6' }}>
          <p style={{ margin: '0 0 8px 0' }}>• Total Purchase Amount: ${totalCost.toFixed(2)}{isSpecialPricingActive && ` (Special test pricing!)`}</p>
          <p style={{ margin: '0 0 8px 0' }}>• ZAR Amount: R{zarAmount.toFixed(2)}</p>
          <p style={{ margin: '0 0 8px 0' }}>• Transaction Fee (10%): R{feeAmount.toFixed(2)}</p>
          <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#10b981' }}>
            • Total to Pay: R{totalZarAmount.toFixed(2)}
          </p>
          <p style={{ margin: 0 }}>• Shares: {shares} {bonusShares > 0 && `(+${bonusShares} bonus = ${totalShares} total)`}</p>
          <PurchaseRewardPreview
            productionCapitalPackages={productionCapitalPackages}
            productionCapitalPackage={productionCapitalPackage}
            productionCapitalPackageQty={productionCapitalPackageQty}
            birthdayPackage={birthdayPackage}
            birthdayQty={birthdayQty}
            phasePrice={currentPhase?.price_per_share}
            paidShares={shares}
            tierBonusShares={bonusShares}
            singleShareActive={singleShareActive}
          />
        </div>
      </div>

      {/* Payment Clearance Notice - MOVED TO TOP */}
      <div style={{
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        border: '2px solid rgba(239, 68, 68, 0.4)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <h3 style={{ color: '#f87171', fontWeight: 'bold', margin: '0 0 12px 0', fontSize: '16px' }}>
          ⚠️ IMPORTANT: Payment Clearance Notice
        </h3>
        <div style={{ color: '#ffffff', fontSize: '14px', lineHeight: '1.6' }}>
          <div style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fbbf24' }}>FNB Payments:</strong>
            <span style={{ marginLeft: '8px' }}>Standard clearance applies (typically same day or next business day)</span>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#f87171' }}>Non-FNB Payments:</strong>
            <span style={{ marginLeft: '8px' }}>You MUST request "Instant Clearance" or "Immediate Payment" when making your deposit at your bank. Without this, your payment may take 3-5 business days to clear.</span>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#10b981' }}>Share Release Process:</strong>
            <span style={{ marginLeft: '8px' }}>Your shares will only be released AFTER the funds are fully cleared and visible in our account, and authorized by our CFO.</span>
          </div>

          <div style={{
            backgroundColor: 'rgba(245, 158, 11, 0.2)',
            padding: '12px',
            borderRadius: '8px',
            marginTop: '12px',
            border: '1px solid rgba(245, 158, 11, 0.3)'
          }}>
            <strong style={{ color: '#fbbf24' }}>💡 To Avoid Delays:</strong>
            <span style={{ marginLeft: '8px' }}>Always request instant clearance when making your payment, and use reference number "{referenceNumber}"</span>
          </div>
        </div>
      </div>

      {/* Banking Details */}
      <div style={{
        backgroundColor: 'rgba(55, 65, 81, 0.5)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <h3 style={{ color: '#fbbf24', fontWeight: 'bold', margin: '0 0 16px 0' }}>
          🏦 BANKING DETAILS:
        </h3>
        <div style={{ color: '#ffffff', fontSize: '14px' }}>
          <div style={{ marginBottom: '12px' }}>
            <span style={{ color: '#ffffff' }}>Bank:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontWeight: 'bold' }}>{bankingDetails.bankName}</span>
              <button
                onClick={() => copyToClipboard(bankingDetails.bankName)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#374151',
                  border: 'none',
                  borderRadius: '4px',
                  color: '#ffffff',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Copy
              </button>
            </div>
          </div>
          
          <div style={{ marginBottom: '12px' }}>
            <span style={{ color: '#ffffff' }}>Account Name:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontWeight: 'bold' }}>{bankingDetails.accountName}</span>
              <button
                onClick={() => copyToClipboard(bankingDetails.accountName)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#374151',
                  border: 'none',
                  borderRadius: '4px',
                  color: '#ffffff',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Copy
              </button>
            </div>
          </div>
          
          <div style={{ marginBottom: '12px' }}>
            <span style={{ color: '#ffffff' }}>Account Number:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontWeight: 'bold', fontFamily: 'monospace' }}>{bankingDetails.accountNumber}</span>
              <button
                onClick={() => copyToClipboard(bankingDetails.accountNumber)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#374151',
                  border: 'none',
                  borderRadius: '4px',
                  color: '#ffffff',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Copy
              </button>
            </div>
          </div>
          
          <div style={{ marginBottom: '12px' }}>
            <span style={{ color: '#ffffff' }}>Branch Code:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontWeight: 'bold', fontFamily: 'monospace' }}>{bankingDetails.branchCode}</span>
              <button
                onClick={() => copyToClipboard(bankingDetails.branchCode)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#374151',
                  border: 'none',
                  borderRadius: '4px',
                  color: '#ffffff',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Copy
              </button>
            </div>
          </div>
          
          <div style={{ marginBottom: '12px' }}>
            <span style={{ color: '#ffffff' }}>Reference:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontWeight: 'bold', fontFamily: 'monospace', color: '#fbbf24' }}>{referenceNumber}</span>
              <button
                onClick={() => copyToClipboard(referenceNumber)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#374151',
                  border: 'none',
                  borderRadius: '4px',
                  color: '#ffffff',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Copy
              </button>
            </div>
          </div>
          
          <div>
            <span style={{ color: '#ffffff' }}>Account Type:</span>
            <span style={{ fontWeight: 'bold', marginLeft: '8px' }}>{bankingDetails.accountType}</span>
          </div>
        </div>
      </div>

      {/* Quick Clearance Reminder */}
      <div style={{
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '20px',
        textAlign: 'center'
      }}>
        <span style={{ color: '#60a5fa', fontSize: '14px', fontWeight: '500' }}>
          💡 Remember: Request "Instant Clearance" from your bank if you're not using FNB
        </span>
      </div>

      {/* Proof Upload */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
          Upload Payment Proof (Bank Statement/Receipt)
        </label>
        <input
          type="file"
          accept="image/*,.pdf"
          onChange={handleFileUpload}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: 'rgba(55, 65, 81, 0.5)',
            border: '2px solid #374151',
            borderRadius: '8px',
            color: 'white',
            fontSize: '14px'
          }}
        />
        {proofFile && (
          <p style={{ color: '#10b981', fontSize: '14px', marginTop: '8px' }}>
            ✅ File selected: {proofFile.name}
          </p>
        )}
      </div>

      {/* Progress Display */}
      {uploadProgress && (
        <div style={{
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '20px',
          color: '#60a5fa',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            border: '3px solid rgba(59, 130, 246, 0.3)',
            borderTop: '3px solid #60a5fa',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          {uploadProgress}
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div style={{
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '20px',
          color: '#f87171'
        }}>
          ❌ {error}
        </div>
      )}

      {/* Gift Purchase Section */}
      <div style={{
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '20px'
      }}>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#60a5fa', fontSize: '15px', fontWeight: '600' }}>
            <input
              type="checkbox"
              checked={isGiftPurchase}
              onChange={(e) => {
                setIsGiftPurchase(e.target.checked);
                if (!e.target.checked) {
                  setRecipientUsername('');
                  setRecipientValidation({
                    isValidating: false,
                    isValid: null,
                    recipientName: null,
                    recipientId: null
                  });
                }
              }}
              disabled={loading}
              style={{
                marginRight: '8px',
                width: '18px',
                height: '18px',
                cursor: 'pointer'
              }}
            />
            🎁 Are you purchasing these shares for someone else?
          </label>
        </div>

        {isGiftPurchase && (
          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(59, 130, 246, 0.2)' }}>
            <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
              Enter the username of the person you're purchasing for: *
            </label>
            <ReferralAutocomplete
              id="bank-gift-recipient"
              name="recipientUsername"
              value={recipientUsername}
              onChange={(e) => handleRecipientUsernameChange(e.target.value)}
              placeholder="recipient_username"
              autoComplete="off"
              disabled={loading}
              validation={
                recipientValidation.isValidating
                  ? { isValid: false, isChecking: true, message: 'Checking username...', type: 'checking' }
                  : recipientValidation.isValid === true
                  ? { isValid: true, isChecking: false, message: `User found: ${recipientValidation.recipientName ?? ''}`, type: 'success' }
                  : recipientValidation.isValid === false && recipientUsername.trim()
                  ? { isValid: false, isChecking: false, message: recipientValidation.recipientName || `Username "${recipientUsername}" not found. Please check the spelling and try again.`, type: 'error' }
                  : null
              }
            />

            <p style={{ color: '#ffffff', fontSize: '14px', marginTop: '8px', marginBottom: 0 }}>
              💡 The shares will be allocated to this user's account. Commissions will be calculated based on their sponsor tree.
            </p>
          </div>
        )}
      </div>

      {/* Submit Button */}
      <button
        onClick={handleShowConfirmation}
        disabled={loading || !proofFile}
        style={{
          width: '100%',
          padding: '16px',
          backgroundColor: (!loading && proofFile) ? '#10b981' : '#374151',
          border: 'none',
          borderRadius: '12px',
          color: 'white',
          fontSize: '18px',
          fontWeight: 'bold',
          cursor: (!loading && proofFile) ? 'pointer' : 'not-allowed',
          marginBottom: '16px'
        }}
      >
        {loading ? 'Submitting Payment...' : 'Submit Payment for Review'}
      </button>

      {/* Back Button */}
      <button
        onClick={onBack}
        disabled={loading}
        style={{
          width: '100%',
          padding: '12px',
          backgroundColor: 'transparent',
          border: '2px solid #374151',
          borderRadius: '8px',
          color: '#ffffff',
          fontSize: '16px',
          cursor: loading ? 'not-allowed' : 'pointer'
        }}
      >
        ← Back to Payment Method
      </button>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#1f2937',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            border: '1px solid #374151',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)'
          }}>
            <h3 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'white',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              ⚠️ Confirm Your Purchase
            </h3>

            <div style={{
              backgroundColor: '#111827',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '24px',
              border: '2px solid #fbbf24'
            }}>
              <p style={{
                color: '#ffffff',
                fontSize: '16px',
                marginBottom: '16px',
                textAlign: 'center'
              }}>
                You are purchasing:
              </p>

              <div style={{
                textAlign: 'center',
                marginBottom: '12px'
              }}>
                <div style={{
                  fontSize: '36px',
                  fontWeight: 'bold',
                  color: '#fbbf24',
                  marginBottom: '4px'
                }}>
                  {shares} {shares === 1 ? 'Share' : 'Shares'}
                </div>
                <PurchaseRewardPreview
                  productionCapitalPackages={productionCapitalPackages}
                  productionCapitalPackage={productionCapitalPackage}
                  productionCapitalPackageQty={productionCapitalPackageQty}
                  birthdayPackage={birthdayPackage}
                  birthdayQty={birthdayQty}
                  phasePrice={currentPhase?.price_per_share}
                  paidShares={shares}
                  tierBonusShares={bonusShares}
                  singleShareActive={singleShareActive}
                />
                <div style={{
                  fontSize: '24px',
                  color: '#10b981',
                  fontWeight: '600'
                }}>
                  ${totalCost.toFixed(2)} USD
                </div>
                <div style={{
                  fontSize: '18px',
                  color: '#60a5fa',
                  marginTop: '8px'
                }}>
                  (R {totalZarAmount.toFixed(2)} ZAR)
                </div>
              </div>

              <div style={{
                borderTop: '1px solid #374151',
                paddingTop: '12px',
                marginTop: '12px'
              }}>
                <div style={{
                  color: '#ffffff',
                  fontSize: '14px',
                  marginBottom: '4px'
                }}>
                  Payment Method: <span style={{ color: '#60a5fa' }}>Bank Transfer</span>
                </div>
                <div style={{
                  color: '#ffffff',
                  fontSize: '14px'
                }}>
                  Reference: <span style={{ color: '#60a5fa', fontFamily: 'monospace' }}>{referenceNumber}</span>
                </div>
              </div>

              {/* Gift Purchase Info */}
              {isGiftPurchase && recipientValidation.recipientName && (
                <div style={{
                  borderTop: '1px solid #374151',
                  paddingTop: '12px',
                  marginTop: '12px',
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  padding: '12px',
                  borderRadius: '6px',
                  marginLeft: '-12px',
                  marginRight: '-12px',
                  marginBottom: '-12px'
                }}>
                  <div style={{
                    color: '#60a5fa',
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    textAlign: 'center'
                  }}>
                    🎁 Gift Purchase
                  </div>
                  <div style={{
                    color: '#ffffff',
                    fontSize: '13px',
                    marginBottom: '4px'
                  }}>
                    Purchasing for: <span style={{ color: '#fbbf24', fontWeight: '600' }}>{recipientValidation.recipientName}</span>
                  </div>
                  <div style={{
                    color: '#ffffff',
                    fontSize: '13px'
                  }}>
                    Payment from: <span style={{ color: '#10b981', fontWeight: '600' }}>{user?.full_name || user?.database_user?.full_name || user?.username || 'You'}</span>
                  </div>
                </div>
              )}
            </div>

            <p style={{
              color: '#ffffff',
              fontSize: '14px',
              marginBottom: '24px',
              textAlign: 'center',
              lineHeight: '1.5'
            }}>
              Please verify the details above are correct before confirming. Make sure you've uploaded the correct proof of payment.
            </p>

            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <button
                onClick={() => setShowConfirmation(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#374151',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitPayment}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Confirm & Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankTransferStep;
